import { MenuItem } from 'primeng/api'

export type AiDeveloperMenuTarget = 'business-rule' | 'expression'

export enum AiDeveloperMenuAction {
  FixWarnings = 'fixWarnings',
  Explain = 'explain',
  GenerateOrEdit = 'generateOrEdit',
}

export type AiDeveloperMenuState = {
  target: AiDeveloperMenuTarget
  isReadonly: boolean
  isLoading: boolean
  isAiGenerationSupportedForLanguage: boolean
  hasContent: boolean
  hasWarnings: boolean
}

export type AiDeveloperMenuCommands = {
  onFixWarnings?: MenuItem['command']
  onExplain?: () => void
  onGenerate?: () => void
}

const iconStyles = 'flex justify-center items-center w-5 h-5 text-yellow-500'

const aiDeveloperMenuItemBuilders: Record<
  AiDeveloperMenuAction,
  (state: AiDeveloperMenuState, commands: AiDeveloperMenuCommands) => MenuItem
> = {
  [AiDeveloperMenuAction.FixWarnings]: (state, commands) => {
    const isFixWarningsDisabled = !state.hasWarnings || state.isReadonly
    const fixWarningsTooltip = state.hasWarnings ? '' : 'No active warnings to fix'

    return {
      label: 'Fix warnings',
      icon: `${iconStyles} ph ph-warning`,
      disabled: state.isLoading || isFixWarningsDisabled,
      tooltip: fixWarningsTooltip,
      automationId: 'ai-developer-fix-warnings-menu-item',
      command: (event): void => commands.onFixWarnings?.(event),
    }
  },
  [AiDeveloperMenuAction.Explain]: (state, commands) => ({
    label: 'Explain',
    icon: `${iconStyles} ph ph-info`,
    disabled: state.isLoading || !state.hasContent,
    automationId: 'ai-developer-explain-menu-item',
    command: (): void => commands.onExplain?.(),
  }),
  [AiDeveloperMenuAction.GenerateOrEdit]: (state, commands) => {
    const isGenerateOrEditDisabledByLanguageSupport =
      state.target === 'expression'
        ? state.hasContent
          ? !state.isAiGenerationSupportedForLanguage
          : false
        : !state.isAiGenerationSupportedForLanguage

    return {
      label: state.hasContent && state.isAiGenerationSupportedForLanguage ? 'Edit' : 'Generate',
      icon: `${iconStyles} ph ph-sparkle`,
      disabled: isGenerateOrEditDisabledByLanguageSupport || state.isLoading || state.isReadonly,
      automationId: 'ai-developer-generate-menu-item',
      command: (): void => commands.onGenerate?.(),
    }
  },
}

export function buildAiDeveloperMenuItems(
  menuActions: AiDeveloperMenuAction[],
  state: AiDeveloperMenuState,
  commands: AiDeveloperMenuCommands
): MenuItem[] {
  return menuActions.map((menuAction) => aiDeveloperMenuItemBuilders[menuAction](state, commands))
}

/** Compatibility wrapper for the new_ab20 scripting editor. */
export function AiDeveloperActionsOptions(
  target: AiDeveloperMenuTarget | undefined,
  config: {
    isReadonly: boolean
    isLoading: boolean
    canGenerateBusinessRule: boolean
    hasContent: boolean
    hasWarnings: boolean
  },
  commands: AiDeveloperMenuCommands
): MenuItem[] {
  if (!target) {
    return []
  }

  const menuActions =
    target === 'business-rule'
      ? [
          AiDeveloperMenuAction.FixWarnings,
          AiDeveloperMenuAction.Explain,
          AiDeveloperMenuAction.GenerateOrEdit,
        ]
      : [AiDeveloperMenuAction.FixWarnings, AiDeveloperMenuAction.GenerateOrEdit]

  return buildAiDeveloperMenuItems(
    menuActions,
    {
      target,
      isReadonly: config.isReadonly,
      isLoading: config.isLoading,
      isAiGenerationSupportedForLanguage: config.canGenerateBusinessRule,
      hasContent: config.hasContent,
      hasWarnings: config.hasWarnings,
    },
    commands
  )
}

export function resetInputFileValueFromEvent(event: Event): void {
  const CLEAR_DELAY = 0
  const inputElement = event.target as HTMLInputElement

  setTimeout(() => (inputElement.value = ''), CLEAR_DELAY)
}

export function hasAiIconTarget(element: HTMLElement): boolean {
  const matchingSelector = 'flx-icon[name="stars-flowx-yellow"]'

  const children = Array.from(element.children)
  const getParents = (el: HTMLElement, parents = []): HTMLElement[] => {
    if (!el.parentElement) {
      return parents
    }

    return getParents(el.parentElement, [...parents, el.parentElement])
  }

  return (
    element.matches(matchingSelector) ||
    children.some((child) => child.matches(matchingSelector)) ||
    getParents(element).some((parent) => parent.matches(matchingSelector))
  )
}
